import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  EnvelopeSimple,
  Scales as Scale,
  ArrowRight,
  CircleNotch as Loader2,
  CheckCircle,
  Briefcase,
  Shield,
  Buildings,
} from '@phosphor-icons/react';
import { useRequestMagicLink } from '@/features/auth/magicLink';
import { isValidEmail, EMAIL_ERROR } from '@/lib/validators';

/**
 * Solicitor sign-in — passwordless.
 * User enters their email; we send a one-time link (15 min TTL, single-use).
 * In dev the link is returned in the response and rendered below the form
 * so it can be tested end-to-end without an email server.
 */
export default function SolicitorLoginPage() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const request = useRequestMagicLink();

  // Live email validation — runs on blur and (once touched) while typing.
  const validateEmail = (value: string) =>
    setEmailError(isValidEmail(value) ? '' : EMAIL_ERROR);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (request.isPending || !email) return;
    if (!isValidEmail(email)) {
      setEmailError(EMAIL_ERROR);
      return;
    }
    setEmailError('');
    request.mutate(email);
  };

  const result = request.data;
  const hasSent = request.isSuccess;

  return (
    <div className="min-h-screen bg-background">
      <div className="grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
        {/* Left — dark brand panel */}
        <div className="hidden flex-col justify-between bg-ink-900 p-12 text-ink-200 lg:flex">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-600 text-white">
              <Scale className="h-5 w-5" weight="duotone" />
            </div>
            <div className="text-lg font-semibold tracking-tight text-white">Jurivoxa</div>
          </Link>

          <div className="max-w-lg">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-brand-400">
              Solicitor Portal
            </div>
            <h1 className="mb-5 text-[34px] font-semibold leading-[1.15] tracking-tight text-white xl:text-[40px]">
              Instruct chambers. Stay in the loop.
            </h1>
            <p className="max-w-md text-[15px] leading-relaxed text-ink-300">
              View your matters, upload briefs, receive opinions and skeletons,
              and pay fee notes — all in one place. No password to remember:
              we'll send you a one-time link each time you sign in.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-ink-800 pt-8">
              {[
                { icon: <Briefcase className="h-4 w-4" />, label: 'Your matters' },
                { icon: <Buildings className="h-4 w-4" />, label: 'Document vault' },
                { icon: <Shield className="h-4 w-4" />, label: 'Secure by design' },
              ].map((f) => (
                <div key={f.label} className="flex flex-col gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-ink-800 text-brand-400">
                    {f.icon}
                  </div>
                  <div className="text-xs text-ink-300">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-ink-500">
            © {new Date().getFullYear()} Jurivoxa · Built for the Bar
          </div>
        </div>

        {/* Right — form */}
        <div className="flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md">
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-600 text-white">
                <Scale className="h-5 w-5" weight="duotone" />
              </div>
              <div className="text-lg font-semibold tracking-tight text-foreground">Jurivoxa</div>
            </div>

            <div className="mb-6">
              <div className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-brand-600 dark:text-brand-400">
                Instructing Solicitor
              </div>
              <h2 className="mb-1 text-[24px] font-semibold tracking-tight text-foreground">
                Sign in with your email
              </h2>
              <p className="text-sm text-muted-foreground">
                We'll send a one-time link that signs you in instantly.
              </p>
            </div>

            {!hasSent && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground">
                    Firm email
                  </label>
                  <div className="relative">
                    <EnvelopeSimple className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      maxLength={255}
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (emailTouched) validateEmail(e.target.value); }}
                      onBlur={(e) => { setEmailTouched(true); validateEmail(e.target.value); }}
                      className="h-11 w-full rounded-md border border-border bg-card pl-10 pr-3.5 text-sm text-foreground transition placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                      placeholder="you@yourfirm.co.uk"
                    />
                  </div>
                  {emailError && <p className="mt-1.5 text-sm text-red-700 dark:text-red-300">{emailError}</p>}
                </div>

                <button
                  type="submit"
                  disabled={request.isPending || !email}
                  className="group inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-5 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {request.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending link…
                    </>
                  ) : (
                    <>
                      Email me a sign-in link
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              </form>
            )}

            {hasSent && result && (
              <div className="rounded-md border border-brand-200 bg-brand-50 p-5">
                <div className="mb-2 flex items-center gap-2 text-brand-700 dark:text-brand-300">
                  <CheckCircle className="h-5 w-5" weight="fill" />
                  <div className="text-sm font-semibold">Check your email</div>
                </div>
                <p className="text-sm text-ink-700">
                  {result.message} The link will expire in 15 minutes and can only be used once.
                </p>

                {result.devLink && (
                  <div className="mt-4 rounded-md border border-dashed border-border bg-card p-3">
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Dev mode — link returned directly
                    </div>
                    <a
                      href={result.devLink}
                      className="block break-all text-xs text-brand-700 dark:text-brand-300 hover:underline"
                    >
                      {result.devLink}
                    </a>
                  </div>
                )}

                <button
                  onClick={() => request.reset()}
                  className="mt-4 text-xs font-medium text-brand-700 dark:text-brand-300 hover:text-brand-800 dark:hover:text-brand-200"
                >
                  Use a different email →
                </button>
              </div>
            )}

            {import.meta.env.DEV && !hasSent && (
              <div className="mt-6 rounded-md border border-dashed border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                <div className="mb-1 font-semibold text-foreground">Dev test solicitor</div>
                <button
                  type="button"
                  onClick={() => setEmail('j.harrington@cliffordchance.com')}
                  className="text-brand-700 dark:text-brand-300 hover:underline"
                >
                  j.harrington@cliffordchance.com
                </button>
                <div className="mt-1 text-xs">
                  James Harrington · Clifford Chance LLP · seeded by{' '}
                  <span className="">pnpm db:seed-dev</span>
                </div>
              </div>
            )}

            <div className="mt-8 border-t border-border pt-6 text-center text-xs text-muted-foreground">
              Chambers staff?{' '}
              <Link to="/login" className="font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
                Sign in with password
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
